import { useEffect } from 'react';
import './IceShardAnimation.css';

export default function IceShardAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle background
    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    // Get positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentRef.current.getBoundingClientRect();
    const battleBgRect = battleBg.getBoundingClientRect();

    // Calculate positions relative to battle background
    const startX = (playerRect.left + playerRect.width / 2) - battleBgRect.left;
    const startY = (playerRect.top + playerRect.height / 2) - battleBgRect.top;
    const endX = (opponentRect.left + opponentRect.width / 2) - battleBgRect.left;
    const endY = (opponentRect.top + opponentRect.height / 2) - battleBgRect.top;

    // Calculate direction and distance
    const dx = endX - startX;
    const dy = endY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Track all created elements
    const elements = [];
    const timers = [];

    // Create ice formation at player (quick buildup for priority move)
    const iceFormation = document.createElement('div');
    iceFormation.className = 'ice-shard-formation';
    iceFormation.style.left = `${startX}px`;
    iceFormation.style.top = `${startY}px`;
    battleBg.appendChild(iceFormation);
    elements.push(iceFormation);

    // Player animation - quick ice gathering
    playerEl.style.animation = 'iceShardPlayerCharge 0.3s ease-out';

    // Create multiple ice shards (3 main shards like Pokemon UNITE)
    const shardCount = 3;
    const mainShardTimer = setTimeout(() => {
      for (let i = 0; i < shardCount; i++) {
        const shardTimer = setTimeout(() => {
          // Create main ice shard projectile
          const shard = document.createElement('div');
          shard.className = 'ice-shard-projectile';
          shard.style.left = `${startX}px`;
          shard.style.top = `${startY}px`;
          
          // Add slight spread to shards
          const spreadAngle = angle + (i - 1) * 0.2; // -0.2, 0, +0.2 radians spread
          const spreadDx = Math.cos(spreadAngle) * distance;
          const spreadDy = Math.sin(spreadAngle) * distance;
          
          shard.style.setProperty('--travel-x', `${spreadDx}px`);
          shard.style.setProperty('--travel-y', `${spreadDy}px`);
          shard.style.setProperty('--rotation', `${spreadAngle}rad`);
          shard.style.setProperty('--shard-delay', `${i * 0.1}s`);
          
          battleBg.appendChild(shard);
          elements.push(shard);
          
          // Create ice trail behind shard
          const trail = document.createElement('div');
          trail.className = 'ice-shard-trail';
          trail.style.left = `${startX}px`;
          trail.style.top = `${startY}px`;
          trail.style.setProperty('--trail-angle', `${spreadAngle}rad`);
          trail.style.setProperty('--trail-length', `${distance * 0.8}px`);
          trail.style.setProperty('--trail-delay', `${i * 0.1}s`);
          
          battleBg.appendChild(trail);
          elements.push(trail);
          
          // Auto-remove shard after animation
          const shardRemoveTimer = setTimeout(() => {
            if (shard.isConnected) {
              shard.remove();
            }
            if (trail.isConnected) {
              trail.remove();
            }
          }, 800);
          
          timers.push(shardRemoveTimer);
        }, i * 100); // Staggered release for rapid-fire effect
        
        timers.push(shardTimer);
      }
      
      // Create additional smaller ice fragments
      for (let i = 0; i < 6; i++) {
        const fragmentTimer = setTimeout(() => {
          const fragment = document.createElement('div');
          fragment.className = 'ice-shard-fragment';
          fragment.style.left = `${startX}px`;
          fragment.style.top = `${startY}px`;
          
          // Random spread around main direction
          const fragmentAngle = angle + (Math.random() - 0.5) * 0.8;
          const fragmentDistance = distance * (0.7 + Math.random() * 0.3);
          const fragmentDx = Math.cos(fragmentAngle) * fragmentDistance;
          const fragmentDy = Math.sin(fragmentAngle) * fragmentDistance;
          
          fragment.style.setProperty('--fragment-x', `${fragmentDx}px`);
          fragment.style.setProperty('--fragment-y', `${fragmentDy}px`);
          fragment.style.setProperty('--fragment-rotation', `${Math.random() * 360}deg`);
          fragment.style.setProperty('--fragment-delay', `${i * 0.05}s`);
          
          battleBg.appendChild(fragment);
          elements.push(fragment);
          
          // Auto-remove fragment after animation
          const fragmentRemoveTimer = setTimeout(() => {
            if (fragment.isConnected) {
              fragment.remove();
            }
          }, 600);
          
          timers.push(fragmentRemoveTimer);
        }, 150 + i * 30); // Start after main shards
        
        timers.push(fragmentTimer);
      }
    }, 200); // Quick start for priority move
    
    timers.push(mainShardTimer);

    // Impact effects on opponent
    const impactTimer = setTimeout(() => {
      if (opponentEl) {
        // Create multiple impact effects for each shard
        for (let i = 0; i < shardCount; i++) {
          const impactEffectTimer = setTimeout(() => {
            const impact = document.createElement('div');
            impact.className = 'ice-shard-impact';
            
            // Slight position variation for each impact
            const impactX = endX + (i - 1) * 15;
            const impactY = endY + (Math.random() - 0.5) * 20;
            
            impact.style.left = `${impactX}px`;
            impact.style.top = `${impactY}px`;
            impact.style.setProperty('--impact-scale', `${0.8 + Math.random() * 0.4}`);
            
            battleBg.appendChild(impact);
            elements.push(impact);
            
            // Create ice splinters on impact
            for (let j = 0; j < 4; j++) {
              const splinter = document.createElement('div');
              splinter.className = 'ice-shard-splinter';
              splinter.style.left = `${impactX}px`;
              splinter.style.top = `${impactY}px`;
              
              const splinterAngle = (j / 4) * Math.PI * 2;
              const splinterDist = 20 + Math.random() * 25;
              splinter.style.setProperty('--splinter-x', `${Math.cos(splinterAngle) * splinterDist}px`);
              splinter.style.setProperty('--splinter-y', `${Math.sin(splinterAngle) * splinterDist}px`);
              splinter.style.setProperty('--splinter-rotation', `${Math.random() * 360}deg`);
              
              battleBg.appendChild(splinter);
              elements.push(splinter);
              
              // Auto-remove splinter
              const splinterRemoveTimer = setTimeout(() => {
                if (splinter.isConnected) {
                  splinter.remove();
                }
              }, 500);
              
              timers.push(splinterRemoveTimer);
            }
            
            // Auto-remove impact
            const impactRemoveTimer = setTimeout(() => {
              if (impact.isConnected) {
                impact.remove();
              }
            }, 400);
            
            timers.push(impactRemoveTimer);
          }, i * 120); // Staggered impacts
          
          timers.push(impactEffectTimer);
        }
        
        // Make opponent recoil from impacts
        opponentEl.style.animation = 'iceShardRecoil 0.6s ease-out';
        
        // Create freeze effect overlay
        const freezeEffect = document.createElement('div');
        freezeEffect.className = 'ice-shard-freeze-effect';
        freezeEffect.style.left = `${endX}px`;
        freezeEffect.style.top = `${endY}px`;
        battleBg.appendChild(freezeEffect);
        elements.push(freezeEffect);
        
        // Reset opponent animation
        const opponentResetTimer = setTimeout(() => {
          if (opponentEl) {
            opponentEl.style.animation = '';
          }
        }, 600);
        
        timers.push(opponentResetTimer);
      }
    }, 500); // Impact timing
    
    timers.push(impactTimer);

    // Complete animation
    const completeTimer = setTimeout(() => {
      // Reset player animation
      if (playerEl) {
        playerEl.style.animation = '';
      }
      
      // Call onComplete callback
      onComplete();
    }, 1200); // Shorter duration for priority move
    
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset animations
      if (playerEl) {
        playerEl.style.animation = '';
      }
      if (opponentEl) {
        opponentEl.style.animation = '';
      }
      
      // Remove all elements
      elements.forEach(element => {
        if (element && element.isConnected) {
          element.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

IceShardAnimation.moveType = "ice";
